<script lang="ts" setup>
import type { PropType } from 'vue';
import type { ICellRendererParams } from 'ag-grid-community';
import ButtonIcon from '@/components/common/ButtonIcon.vue';

export type ButtonParams = {
    visible: boolean;
    tooltip: string;
    click: Function;
    icon: (params: ICellRendererParams) => string;
    disabled?: (params: ICellRendererParams) => boolean;
};

/**********
 * Init
 **********/
type ExtendedParams = ICellRendererParams & { buttons: ButtonParams[] };

const props = defineProps({
    params: {
        type: Object as PropType<ExtendedParams>,
        required: true
    }
});

const click = (button: ButtonParams): void => {
    if (isDisabled(button.disabled) === true) return;
    if (button.click != null) {
        button.click();
    }
};

function icon(icon: string | Function): string {
    return typeof icon === 'function' ? icon(props.params.value) : icon;
}

function isDisabled(disabled?: boolean | Function): boolean {
    if (disabled == null) return false;
    return typeof disabled === 'function' ? disabled(props.params) : disabled;
}
</script>
<template>
    <div class="ag-buttons-container flex-container">
        <ButtonIcon
            v-for="(button, index) of props.params.buttons"
            :key="index"
            :class="[{ 'hidden': !button.visible }]"
            :icon="icon(button.icon)"
            icon-classes="sy-icon-small"
            :disabled="isDisabled(button.disabled)"
            :tooltip="{ content: button.tooltip, placement: 'left' }"
            @click="click(button)"
        />
    </div>
</template>

<style lang="scss">
.ag-buttons-container {
    justify-content: center;
    align-items: center;
    flex: 1;
    flex-direction: row;

    ::v-deep(.button > span) {
        line-height: 1px;
    }
}
</style>
